import { httpClient2 } from '../../infrastructure/admi-client/http-client';
import {
    Building,
    BuildingImportResponse,
    BuildingsPage,
    BuildingsMetadataPage,
    BuildingsSearchV2Response,
    BuildingSearch,
    LocationSearch,
    RealEstate,
    RealEstateAssetSummary,
} from './buildings.dto';

export interface RealEstateWithAssets extends RealEstate {
    realEstateAssets: RealEstateAssetSummary[];
}

const buildSearchParams = (dataSearch: LocationSearch) => ({
    lat: dataSearch.location.lat,
    lon: dataSearch.location.lng,
    radiusMeters: dataSearch.radius,
    size: 500,
});

export const admiBuildingsService = {
    /**
     * v1 buildings search — flat `Page<Building>` shape.
     * Used by Onsite, PV Analysis, Document Generation — any feature that doesn't need REA info.
     */
    async loadBuildingsData(dataSearch: LocationSearch): Promise<RealEstate> {
        const response = await httpClient2.get<BuildingsPage>('/v1/buildings/search', {
            params: buildSearchParams(dataSearch),
        });

        return {
            count: response.data.totalElements,
            locationSearch: {
                lat: dataSearch.location.lat,
                lng: dataSearch.location.lng,
                radius: dataSearch.radius,
            },
            buildings: response.data.content,
        };
    },

    /**
     * v2 buildings search — returns buildings plus their real-estate-asset associations.
     * Used exclusively by the Real Estate Asset Management feature.
     */
    async loadBuildingsDataWithAssetsV2(dataSearch: LocationSearch): Promise<RealEstateWithAssets> {
        const response = await httpClient2.get<BuildingsSearchV2Response>('/v2/buildings/search', {
            params: buildSearchParams(dataSearch),
        });

        return {
            count: response.data.count,
            locationSearch: {
                lat: dataSearch.location.lat,
                lng: dataSearch.location.lng,
                radius: dataSearch.radius,
            },
            buildings: response.data.data.buildings,
            realEstateAssets: response.data.data.realEstateAssets,
        };
    },

    async loadBuildingData(search: BuildingSearch): Promise<Building> {
        const response = await httpClient2.get<Building>(`/v1/buildings/${search.uuid}`);
        return response.data;
    },

    async loadBuildingsMetaData(dataSearch: LocationSearch): Promise<BuildingsMetadataPage> {
        const response = await httpClient2.get<BuildingsMetadataPage>('/v1/buildings/metadata/search', {
            params: buildSearchParams(dataSearch),
        });

        return response.data;
    },

    async importBuilding(filePath: string, buildingCode: string, municipalityCode: string, id?: string) {
        const response = await httpClient2.post<BuildingImportResponse>('/v1/buildings/import', {
            filePath,
            buildingCode,
            municipalityCode,
            ...(id && { id }),
        });
        return response.data;
    },
};
